package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.RelSegDossier
import br.com.htms.inspeccaocampo.data.RelSegMeses

/** Relatório de Segurança (REM - Segurança): relatórios mensais por obra, DDS, treinamentos e dashboard SMS. */
object PlanilhaRelSeg {
    private fun periodo(d: RelSegDossier) = "${d.ano}-${d.mes.toString().padStart(2, '0')}"

    fun build(dossies: List<RelSegDossier>): ByteArray {
        val lista = dossies.sortedWith(compareByDescending<RelSegDossier> { it.ano }.thenByDescending { it.mes }.thenBy { it.obra.lowercase() })
        fun n(s: String): Double? = PlanKit.numero(s)

        val colsRel = listOf(
            ColPlan("OBRA", 30), ColPlan("ANO", 7, TipoCol.NUMERO), ColPlan("MÊS", 11), ColPlan("PERÍODO", 9),
            ColPlan("CONTRATO", 16), ColPlan("CLIENTE", 22), ColPlan("GRAU DE RISCO", 8),
            ColPlan("EFETIVO HTMS", 10, TipoCol.NUMERO), ColPlan("EFETIVO TERCEIRO", 10, TipoCol.NUMERO),
            ColPlan("DESVIOS", 9, TipoCol.NUMERO), ColPlan("APR", 8, TipoCol.NUMERO), ColPlan("PT", 8, TipoCol.NUMERO),
            ColPlan("DDS", 8, TipoCol.NUMERO), ColPlan("TREINAMENTOS", 11, TipoCol.NUMERO), ColPlan("CHECKLIST", 10, TipoCol.NUMERO),
            ColPlan("ACIDENTES FATAIS", 10, TipoCol.NUMERO), ColPlan("ACIDENTES", 10, TipoCol.NUMERO),
            ColPlan("LESÕES", 9, TipoCol.NUMERO), ColPlan("QUASE ACIDENTES", 10, TipoCol.NUMERO),
            ColPlan("DDS REGISTRADOS", 10, TipoCol.NUMERO), ColPlan("TREINAMENTOS REGISTRADOS", 12, TipoCol.NUMERO),
            ColPlan("RESPONSÁVEL", 18), ColPlan("ATUALIZADO EM", 12, TipoCol.DATA)
        )
        val rel = PlanKit.abaDados(
            "RELATÓRIOS", "Relatório de Segurança · gestão SMS por obra e mês",
            "Um relatório por linha. Filtre por obra, ano ou mês: o DASHBOARD acompanha o filtro.",
            colsRel,
            lista.map { d ->
                val g = d.payload.gestaoSms
                val dg = d.payload.dadosGerais
                listOf(
                    d.obra, d.ano, RelSegMeses.rotulo(d.mes), periodo(d), dg.contrato, dg.cliente, dg.grauRisco,
                    n(dg.efetivoHtms), n(dg.efetivoTerceiro), g.desviosGerais, n(g.apr), n(g.pt), n(g.dds), n(g.treinamentos),
                    n(g.checklist), g.acidentesFatais, g.acidentes, g.lesoes, g.quaseAcidentes,
                    d.payload.dds.count { it.temaData.isNotBlank() },
                    d.payload.treinamentos.count { it.treinamento.isNotBlank() },
                    d.username.orEmpty(), PlanData.deMs(d.updatedAt)
                )
            },
            cor = "C62828"
        )

        val dds = PlanKit.abaDados(
            "DDS", "DDS registrados", "Temas de DDS lançados em cada relatório.",
            listOf(ColPlan("OBRA", 30), ColPlan("PERÍODO", 9), ColPlan("Nº", 6, TipoCol.NUMERO), ColPlan("TEMA / DATA", 60), ColPlan("COM FOTO", 9)),
            lista.flatMap { d ->
                d.payload.dds.mapIndexedNotNull { i, e ->
                    if (e.temaData.isBlank()) null else listOf(d.obra, periodo(d), i + 1, e.temaData, e.fotoRef.isNotBlank())
                }
            },
            cor = "EF6C00"
        )
        val tre = PlanKit.abaDados(
            "TREINAMENTOS", "Treinamentos registrados", "Treinamentos informados nos relatórios.",
            listOf(
                ColPlan("OBRA", 30), ColPlan("PERÍODO", 9), ColPlan("TREINAMENTO", 36), ColPlan("DATA", 12, TipoCol.DATA),
                ColPlan("Nº DE PESSOAS", 10, TipoCol.NUMERO), ColPlan("PARTICIPANTES", 40), ColPlan("DESCRIÇÃO", 40), ColPlan("FOTOS", 7, TipoCol.NUMERO)
            ),
            lista.flatMap { d ->
                d.payload.treinamentos.filter { it.treinamento.isNotBlank() }.map { t ->
                    listOf(
                        d.obra, periodo(d), t.treinamento, PlanData.de(t.data), n(t.numPessoas), t.nomes, t.descricao,
                        t.fotos.count { it.isNotBlank() }
                    )
                }
            },
            cor = "2E7D32"
        )

        fun soma(sel: (RelSegDossier) -> Number?) = lista.sumOf { sel(it)?.toDouble() ?: 0.0 }
        val g = { d: RelSegDossier -> d.payload.gestaoSms }
        val kpis = listOf(
            KpiPlan("Relatórios", rel.contar(), lista.size.toDouble()),
            KpiPlan("Desvios", rel.somar("DESVIOS"), soma { g(it).desviosGerais }, SharedOoxmlWriter.STYLE_CRIT_MEDIA),
            KpiPlan("Acidentes fatais", rel.somar("ACIDENTES FATAIS"), soma { g(it).acidentesFatais }, SharedOoxmlWriter.STYLE_CRIT_ALTA),
            KpiPlan("Acidentes", rel.somar("ACIDENTES"), soma { g(it).acidentes }, SharedOoxmlWriter.STYLE_CRIT_ALTA),
            KpiPlan("Lesões", rel.somar("LESÕES"), soma { g(it).lesoes }, SharedOoxmlWriter.STYLE_CRIT_MEDIA),
            KpiPlan("Quase acidentes", rel.somar("QUASE ACIDENTES"), soma { g(it).quaseAcidentes }, SharedOoxmlWriter.STYLE_CRIT_MEDIA),
            KpiPlan("Efetivo médio HTMS", "IFERROR(SUBTOTAL(1,${rel.faixa("EFETIVO HTMS")}),0)",
                lista.mapNotNull { n(it.payload.dadosGerais.efetivoHtms) }.let { if (it.isEmpty()) 0.0 else it.average() }),
            KpiPlan("Efetivo médio terceiro", "IFERROR(SUBTOTAL(1,${rel.faixa("EFETIVO TERCEIRO")}),0)",
                lista.mapNotNull { n(it.payload.dadosGerais.efetivoTerceiro) }.let { if (it.isEmpty()) 0.0 else it.average() }),
            KpiPlan("APR", rel.somar("APR"), soma { n(g(it).apr) }),
            KpiPlan("PT", rel.somar("PT"), soma { n(g(it).pt) }),
            KpiPlan("DDS", rel.somar("DDS"), soma { n(g(it).dds) }),
            KpiPlan("Treinamentos", rel.somar("TREINAMENTOS"), soma { n(g(it).treinamentos) }),
            KpiPlan("Checklists", rel.somar("CHECKLIST"), soma { n(g(it).checklist) }),
            KpiPlan(
                "Pessoas treinadas", tre.somar("Nº DE PESSOAS"),
                lista.sumOf { d -> d.payload.treinamentos.filter { it.treinamento.isNotBlank() }.sumOf { n(it.numPessoas) ?: 0.0 } }
            )
        )

        val piramide = listOf(
            "Acidentes fatais" to "ACIDENTES FATAIS", "Acidentes" to "ACIDENTES", "Lesões" to "LESÕES",
            "Quase acidentes" to "QUASE ACIDENTES", "Desvios" to "DESVIOS"
        )
        val cachePiramide = mapOf(
            "Acidentes fatais" to soma { g(it).acidentesFatais }, "Acidentes" to soma { g(it).acidentes },
            "Lesões" to soma { g(it).lesoes }, "Quase acidentes" to soma { g(it).quaseAcidentes }, "Desvios" to soma { g(it).desviosGerais }
        )
        val atividades = listOf("APR" to "APR", "PT" to "PT", "DDS" to "DDS", "Treinamentos" to "TREINAMENTOS", "Checklists" to "CHECKLIST")
        val cacheAtiv = mapOf(
            "APR" to soma { n(g(it).apr) }, "PT" to soma { n(g(it).pt) }, "DDS" to soma { n(g(it).dds) },
            "Treinamentos" to soma { n(g(it).treinamentos) }, "Checklists" to soma { n(g(it).checklist) }
        )
        val ocorrPorObra = PlanKit.soma(lista, { it.obra }) {
            g(it).let { s -> ((s.acidentesFatais ?: 0) + (s.acidentes ?: 0) + (s.lesoes ?: 0) + (s.quaseAcidentes ?: 0)).toDouble() }
        }
        val obraR = rel.faixa("OBRA")
        val blocos = listOf(
            BlocoPlan("Pirâmide de segurança", "Nível", piramide.map { it.first to cachePiramide.getValue(it.first) },
                { rot -> rel.somar(piramide.first { it.first == rot }.second) }, limite = 10),
            BlocoPlan("Desvios por obra", "Obra", PlanKit.soma(lista, { it.obra }) { (g(it).desviosGerais ?: 0).toDouble() },
                { rel.somarSe("OBRA", it, "DESVIOS") }, formulaTotal = rel.somar("DESVIOS")),
            BlocoPlan("Desvios por mês", "Período", PlanKit.soma(lista, { periodo(it) }) { (g(it).desviosGerais ?: 0).toDouble() }.sortedBy { it.first },
                { rel.somarSe("PERÍODO", it, "DESVIOS") }, limite = 24),
            BlocoPlan("Acidentes, lesões e quase acidentes por obra", "Obra", ocorrPorObra,
                {
                    "SUMPRODUCT(($obraR=${PlanKit.texto(it)})*(${rel.faixa("ACIDENTES FATAIS")}+${rel.faixa("ACIDENTES")}+" +
                        "${rel.faixa("LESÕES")}+${rel.faixa("QUASE ACIDENTES")})*${rel.visiveis()})"
                }),
            BlocoPlan("Atividades de SMS", "Atividade", atividades.map { it.first to cacheAtiv.getValue(it.first) },
                { rot -> rel.somar(atividades.first { it.first == rot }.second) }, SharedExcelChartType.PIE),
            BlocoPlan("Relatórios por obra", "Obra", PlanKit.contagem(lista) { it.obra },
                { rel.contarSe("OBRA", it) }, formulaTotal = rel.contar()),
            BlocoPlan("Pessoas treinadas por obra", "Obra",
                PlanKit.soma(lista.flatMap { d -> d.payload.treinamentos.filter { it.treinamento.isNotBlank() }.map { d.obra to (n(it.numPessoas) ?: 0.0) } },
                    { it.first }) { it.second },
                { tre.somarSe("OBRA", it, "Nº DE PESSOAS") }, formulaTotal = tre.somar("Nº DE PESSOAS"))
        )
        val dash = PlanKit.dashboard(
            "DASHBOARD", "Dashboard · Relatório de Segurança",
            "Filtre a aba RELATÓRIOS (obra, ano, mês) e o dashboard acompanha. Gerado em ${PlanData.geradoEm()}.",
            kpis, blocos, cor = "B71C1C"
        )
        return PlanKit.gerar(listOf(dash, rel.sheet, dds.sheet, tre.sheet))
    }
}
